// AIInvoiceAnalysisCard.jsx
import { useState, useEffect } from "react";
import {
  DollarSign,
  Calendar,
  TrendingUp,
  Shield,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useInvoiceAnalysis } from "../../hooks/aiHooks/useInvoiceAnalysis";
import AIThinkingLoader from "../common/AIThinkingLoader";

const AIInvoiceAnalysisCard = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { getLatestInvoiceAnalysis } = useInvoiceAnalysis();

  useEffect(() => {
    fetchAnalysisData();
  }, []);

  const fetchAnalysisData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getLatestInvoiceAnalysis();
      if (result.success && result.data) {
        setAnalysis(result.data);
      }
    } catch (err) {
      console.error("Error fetching invoice analysis:", err);
      setError("Failed to load AI analysis data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getRecommendationActionColor = (action) => {
    switch (action?.toUpperCase()) {
      case "CONSIDER_RESERVATIONS":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "OPTIMIZE_VMS":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "REVIEW_SHARED_COSTS":
        return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (loading) {
    return <AIThinkingLoader />;
  }

  if (error) {
    return null;
  }

  if (!analysis) {
    return null;
  }

  return (
    <div
      className="rounded-xl border border-primary-cyan/20 overflow-hidden my-4 mx-2 sm:mx-4 md:mx-8"
      style={{ backgroundColor: "#10182b" }}
    >
      <div
        className="px-3 sm:px-5 py-3 border-b border-primary-cyan/20 sticky z-20"
        style={{ backgroundColor: "#10182b" }}
      >
        <div className="flex flex-wrap justify-between items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary-cyan/20 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-primary-cyan" />
            </div>
            <span className="text-primary-cyan text-sm sm:text-base font-bold tracking-wide">
              AI INVOICE INSIGHTS
            </span>
            <span className="text-[10px] sm:text-[11px] text-gray-500 uppercase tracking-wider font-medium">
              Last 12 Months Analysis
            </span>
          </div>
          <button
            onClick={toggleExpand}
            className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg bg-primary-cyan/10 hover:bg-primary-cyan/20 transition-all duration-200 text-primary-cyan text-xs sm:text-sm font-medium"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Show Less</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Show Details</span>
              </>
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-3 sm:p-5" style={{ backgroundColor: "#10182b" }}>
          {analysis.summary && (
            <div
              className="mb-5 rounded-xl p-3 sm:p-4"
              style={{ backgroundColor: "#1c2436" }}
            >
              <p className="text-gray-200 text-xs sm:text-sm leading-relaxed">
                {analysis.summary}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div
              className="rounded-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-lg hover:shadow-primary-cyan/10 hover:scale-[1.01]"
              style={{ backgroundColor: "#1c2436" }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-sm font-bold uppercase tracking-wide">
                  Monthly Insights
                </span>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">Average Monthly</span>
                  <span className="text-white font-semibold">
                    {formatCurrency(analysis.averageMonthlyCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">Peak Month</span>
                  <span className="text-red-400 font-semibold break-words text-right">
                    {analysis.peakMonthName || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">Lowest Month</span>
                  <span className="text-green-400 font-semibold break-words text-right">
                    {analysis.lowestMonthName || "N/A"}
                  </span>
                </div>
                {analysis.seasonality && (
                  <div className="pt-2 border-t border-gray-700">
                    <p className="text-gray-500 text-xs sm:text-sm break-words">
                      {analysis.seasonality}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div
              className="rounded-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-lg hover:shadow-primary-cyan/10 hover:scale-[1.01]"
              style={{ backgroundColor: "#1c2436" }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-sm font-bold uppercase tracking-wide">
                  Last Two Months
                </span>
              </div>
              <p className="text-gray-200 text-xs sm:text-sm leading-relaxed font-medium break-words">
                {analysis.lastTwoMonthsAnalysis || "No data available"}
              </p>
              <div className="mt-3 pt-2 border-t border-gray-700">
                <div className="flex items-center gap-1">
                  {analysis.predictionAnalysis && (
                    <p className="text-gray-500 text-xs sm:text-sm break-words">
                      {analysis.predictionAnalysis}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div
            className="rounded-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-lg hover:shadow-primary-cyan/10 hover:scale-[1.01]"
            style={{ backgroundColor: "#1c2436" }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary-cyan" />
              </div>
              <span className="text-primary-cyan text-sm font-bold uppercase tracking-wide">
                Recommendation
              </span>
            </div>
            <p className="text-gray-200 text-sm sm:text-base leading-relaxed font-medium break-words">
              {analysis.recommendationSuggestion ||
                "No recommendation available"}
            </p>
            {analysis.recommendationAction &&
              analysis.recommendationAction !== "NONE" && (
                <div className="mt-4">
                  <span
                    className={`text-xs px-2 sm:px-3 py-1.5 rounded-full font-semibold ${getRecommendationActionColor(analysis.recommendationAction)}`}
                  >
                    Action: {analysis.recommendationAction.replace(/_/g, " ")}
                  </span>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIInvoiceAnalysisCard;